import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatSelectChange } from "@angular/material/select";
import { DocumentService } from "../../../services/internal-services/document.service";
import { IFeatureMeasurement } from "../../../interfaces/i-feature-measurement";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { IMeasurementFeature } from "../../../interfaces/i-measurement-feature";
import { MeasurementFeature } from "../../../model/measurement-feature";
import { DxmDownloadStatus } from "../../../model/dxm-download-status";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { MapService } from "../../../services/internal-services/map.service";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";
import { Subscription } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-measurement-info",
  templateUrl: "./measurement-info.component.html",
  styleUrls: ["./measurement-info.component.scss"],
  standalone: false,
})
export class MeasurementInfoComponent implements AfterViewInit, OnInit, OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  @Input("status3D") status3D: string;

  @Input("oblique") oblique: boolean;

  @Input("inside") inside: boolean;

  @Input("mode3D") mode3D: boolean;

  @Input() debugMode = false;

  @Input()
  set measuresSelected(features: IMeasurementFeature[]) {
    this.handleFeatures(features);
  }

  metricLengthUnits = ViewerConstants.LENGTH_METRIC_UNITS;

  imperialLengthUnits = ViewerConstants.LENGTH_IMPERIAL_UNITS;

  metricAreaUnits = ViewerConstants.AREA_METRIC_UNITS;

  imperialAreaUnits = ViewerConstants.AREA_IMPERIAL_UNITS;

  slopeUnits = ViewerConstants.SLOPE_UNITS;

  enableLength = false;

  enableHeight = false;

  enableInfo = false;

  showLengths = false;

  showLengthSegments = false;

  showCircumference = false;

  showRadius = false;

  showSlopes = false;

  enableElevation = false;

  enableRelativeElevation = false;

  enableGroundElevation = false;

  enableLocation = false;

  enableArea = false;

  enableSlope = false;

  enableRadius = false;

  enableCircumference = false;

  showAreas = false;

  showPlaceholder = true;

  slopePlaceholder = false;

  showHeights = false;

  systems = ViewerConstants.MEASUREMENT_SYSTEMS.SYSTEMS;

  unitLengthSelected: string;

  unitAreaSelected: string;

  unitSlopeSelected: string;

  private globalSubscriptions: Subscription[] = [];

  // @ts-ignore
  private _selectedFeature: any;

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    public cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.unitLengthSelected = this.viewerService.getMeasurementLengthUnit();
    this.unitAreaSelected = this.viewerService.getMeasurementAreaUnit();
    this.unitSlopeSelected = this.viewerService.getMeasurementSlopeUnit();
    if (!this.unitLengthSelected) {
      this.unitLengthSelected = this.viewerService.getMeasurementSystemId().toString();
    }
    if (!this.unitAreaSelected) {
      this.unitAreaSelected = this.viewerService.getMeasurementSystemId().toString();
    }
    if (!this.unitSlopeSelected) {
      // eslint-disable-next-line prefer-destructuring
      this.unitSlopeSelected = ViewerConstants.SLOPE_UNITS[0];
    }

    this.globalSubscriptions.push(
      this.viewerService.measurementLengthUnit$.subscribe((unit) => {
        if (unit) {
          this.unitLengthSelected = unit;
          this.cdr.markForCheck();
        }
      }),
      this.viewerService.measurementAreaUnit$.subscribe((unit) => {
        if (unit) {
          this.unitAreaSelected = unit;
          this.cdr.markForCheck();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.globalSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.checkFeaturesLength();
    this.measurementStatus();
  }

  /**
   * if the measurement layer only have one feature we auto select this feature
   */
  checkFeaturesLength() {
    const features = this.mapService
      .getMeasurementLayer()
      .getSource()
      .getFeatures()
      .filter((f) => f.get("type") === "measurement");

    if (features?.length === 1) {
      const feature = features[0];
      this.viewerService.selectMeasure(feature.getId());
    }
  }

  handleFeatures(features: IMeasurementFeature[]) {
    if (features?.length === 1) {
      const feature = features[0];
      this.showPlaceholder = false;
      this.selectedFeature = feature;
    } else {
      this.selectedFeature = null;
      this.resetFeatureInfo();
    }
  }

  resetFeatureInfo() {
    this.showPlaceholder = true;
    this.enableLocation = false;
    this.enableElevation = false;
    this.enableRelativeElevation = false;
    this.enableGroundElevation = false;
    this.enableArea = false;
    this.enableHeight = false;
    this.enableLength = false;
    this.enableInfo = false;
    this.enableSlope = false;
  }

  copyToClipboard(event: any, value: any) {
    DocumentService.copyTextToClipboard(value);
  }

  measurementStatus() {
    this.showAreas = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.AREA);
    this.showLengths = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.LENGTH);

    this.showLengthSegments = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.LENGTH_SEGMENTS);

    this.showCircumference = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.CIRCUMFERENCE);
    this.showRadius = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.RADIUS);
    this.showSlopes = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.SLOPE);
    this.showHeights = this.checkMeasurementTypeStatus(ViewerConstants.MEASURE_TYPE.HEIGHT);
  }

  checkMeasurementTypeStatus(type: any): boolean {
    const measurementTools = this.viewerService.getMeasurementTools();
    if (type === null || type === undefined || !measurementTools || measurementTools.length === 0) {
      return false;
    }

    const toolsWithMeasurementTypeActive = measurementTools
      .filter((tool) => tool.internalTool.visibleMeasures && tool.internalTool.visibleMeasures.length > 0)
      .map((tool) => tool.internalTool.visibleMeasures)
      .filter((tool) => {
        const measmentActive = tool.filter(
          // eslint-disable-next-line eqeqeq
          (measurement) => type == measurement,
        );
        return measmentActive && measmentActive.length > 0;
      });
    return toolsWithMeasurementTypeActive && toolsWithMeasurementTypeActive.length > 0;
  }

  toggleLengths() {
    this.showLengths = !this.showLengths;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.LENGTH);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  toggleLengthSegments() {
    this.showLengthSegments = !this.showLengthSegments;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.LENGTH_SEGMENTS);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  toggleCircumference() {
    this.showCircumference = !this.showCircumference;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.CIRCUMFERENCE);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  toggleRadius() {
    this.showRadius = !this.showRadius;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.RADIUS);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  toggleHeights() {
    this.showHeights = !this.showHeights;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.HEIGHT);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  toggleSlopes() {
    this.showSlopes = !this.showSlopes;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.SLOPE);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  changeLengthUnits(event: MatSelectChange) {
    this.unitLengthSelected = event.value;
    this.viewerService.updateMeasurementLengthUnit(this.unitLengthSelected);

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_CHANGE_UNITS,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  changeAreaUnits(event: MatSelectChange) {
    this.unitAreaSelected = event.value;
    this.viewerService.updateMeasurementAreaUnit(this.unitAreaSelected);

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_CHANGE_UNITS,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  changeSlopeUnits(event: MatSelectChange) {
    this.unitSlopeSelected = event.value;
    this.viewerService.updateMeasurementSlopeUnit(this.unitSlopeSelected);

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_CHANGE_UNITS,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  toggleAreas() {
    this.showAreas = !this.showAreas;
    this.viewerService.getMeasurementTools().map((tool) => {
      tool.toggleMeasurementType(ViewerConstants.MEASURE_TYPE.AREA);
      return tool;
    });

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.MEASUREMENT_SHOW_OR_HIDE_VALUES,
      GoogleAnalyticsConstantsCategory.MEASUREMENT_TOOLBAR,
      true,
    );
  }

  set selectedFeature(feature: IMeasurementFeature) {
    this._selectedFeature = feature
      ? MeasurementFeature(feature.geometry, feature.id, feature.type, feature.properties)
      : null;
    this.handleFeature(this._selectedFeature);
  }

  get selectedFeature(): IMeasurementFeature {
    return this._selectedFeature;
  }

  handleFeature(feature: IMeasurementFeature) {
    if (!this.checkSupportedFeature(feature)) {
      return;
    }
    if (feature.hasMeasurement()) {
      this.handleMeasurements(feature.getMeasurement());
    } else {
      this.resetFeatureInfo();
    }
  }

  handleMeasurements(measurement: IFeatureMeasurement) {
    this.enableHeight = !!measurement.height;
    this.enableLength = !!measurement.length;
    this.enableArea = !!measurement.area;
    this.enableElevation = !!measurement.elevation;
    this.enableRelativeElevation = !!measurement.relativeElevation;
    this.enableGroundElevation = !!measurement.ground;
    this.enableLocation = !!measurement.location;
    this.enableInfo = !!measurement.info;
    this.enableSlope = !!measurement.slopes || !!measurement.slope;
    this.enableCircumference = !!measurement.circumference;
    this.enableRadius = !!measurement.radius;
  }

  checkSupportedFeature(feature: IMeasurementFeature): boolean {
    if (!feature || !feature.geometry) {
      return false;
    }
    return ViewerConstants.MEASUREMENT_TYPES.indexOf(feature.getType()) !== -1;
  }

  getInfoMessage(): string {
    if (this.noData() && !this.enableHeight) {
      this.showPlaceholder = true;
      return "3D measuring tools are not compatible with this collection.";
    }

    if (this.downloadingData()) {
      return "3D measuring tools not yet available.";
    }
    return null;
  }

  getInfoAccentMessage(): string {
    if (this.noData() && !this.enableHeight) {
      return "Try a different collection on the timeline";
    }

    if (this.downloadingData()) {
      return "Downloading data";
    }
    return null;
  }

  noData(): boolean {
    return this.mode3D && this.oblique && this.status3D === DxmDownloadStatus.stop;
  }

  downloadingData(): boolean {
    return this.mode3D && this.status3D === DxmDownloadStatus.downloading;
  }

  getLocation(): string {
    return `${this.selectedFeature.getLocation()[1]}, ${this.selectedFeature.getLocation()[0]}`;
  }

  getSlopeMessage(): string {
    if (!this.selectedFeature) {
      return "Select a line or shape to get info";
    }
    if (this.selectedFeature.getSlopes() && this.selectedFeature.getSlopes().length > 1) {
      return "Can’t show pitch for lines with multiple segments";
    }
    if (!this.selectedFeature.isPlanar()) {
      this.slopePlaceholder = true;
      return "Can’t show pitch for geometries that are not flat";
    }
    this.slopePlaceholder = false;

    return null;
  }
}
